import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { colors, typography, spacing, media } from "../styles/designSystem";
import { PageHeadline, SectionHeadline, TerminalLabel } from "../components/Typography";
import NDADisclosure from "../components/NDADisclosure";
import ContactForm from "../components/ContactForm";
import { ContrastPair, CycleFlow, StepGrid } from "../components/diagrams/primitives";

/**
 * How I Build.
 *
 * Replaces the old design-thinking process page. The rule for this page is that it
 * makes no claim it cannot point at: every practice below links to the case study
 * that shows it, or to something in this repository the reader can check.
 */

const PageWrapper = styled.div`
    /* Narrower than a case study: there is no sidebar here, so the panels are held
       close to the prose measure instead of stranding the text on the left. */
    max-width: 920px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const Meta = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};
    margin: ${spacing[500]} 0 ${spacing[600]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 72ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Pull = styled.blockquote`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
`;

const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

/* === Evidence rows ===
   The whole argument of this page rests on being checkable, so each claim is a link
   to the work behind it rather than an assertion sitting on its own. */

const EvidenceList = styled.div`
    display: flex;
    flex-direction: column;
    border-top: 1px solid ${({ theme }) => theme.borderLight};
    margin: ${spacing[700]} 0;
`;

const EvidenceRow = styled(Link)`
    display: grid;
    grid-template-columns: 9rem minmax(0, 1fr) auto;
    align-items: baseline;
    gap: ${spacing[500]};
    padding: ${spacing[500]} ${spacing[400]};
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    color: ${({ theme }) => theme.text};
    transition: background 0.14s ease-out;

    &:hover {
        background: ${({ theme }) => theme.surfaceHover};
    }

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
        gap: ${spacing[200]};
    }
`;

const Where = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
`;

const What = styled.span`
    font-size: ${typography.fontSize[200]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
`;

// Inverts on row hover the same way the employment record does, so the row reads as
// one selectable entry rather than a sentence with a link buried in it.
const Open = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wider};
    color: ${({ theme }) => theme.primary};
    white-space: nowrap;
    /* Padding is always applied so igniting on hover never reflows the row. */
    padding: 0.25rem calc(0.5rem - ${typography.letterSpacing.wider}) 0.25rem 0.5rem;
    border: 1px solid transparent;
    transition: background 0.14s ease-out, color 0.14s ease-out,
                border-color 0.14s ease-out, box-shadow 0.14s ease-out;

    &::before {
        content: "[";
        opacity: 0.5;
        margin-right: 0.3rem;
    }

    &::after {
        content: "]";
        opacity: 0.5;
        margin-left: 0.3rem;
    }

    ${EvidenceRow}:hover & {
        background: ${({ theme }) => theme.primary};
        border-color: ${({ theme }) => theme.primary};
        color: ${({ theme }) => theme.background};
        text-shadow: none;
        box-shadow: 0 0 14px ${({ theme }) => theme.glow};
    }

    @media (max-width: 720px) {
        justify-self: start;
        margin-top: ${spacing[200]};
    }
`;

const CONSTRAINTS = [
    {
        to: "/work/spectrum",
        where: "Spectrum",
        color: colors.primary[500],
        what: "Agents were reading the interface while a customer waited on the line. Handle time was the constraint, so the architecture had to answer inside the call.",
    },
    {
        to: "/work/NsaBrands",
        where: "NSA Brands",
        color: colors.cyan[300],
        what: "A rental moves through states whether or not anyone is looking at a screen. The state machine came first; the interfaces were views onto it.",
    },
    {
        to: "/work/att",
        where: "AT&T",
        color: colors.amber[400],
        what: "One design system with three different consumers. Consistency had to be enforceable rather than requested, so the rules became checkable.",
    },
    {
        to: "/work/Caterpillar",
        where: "Caterpillar",
        color: colors.emerald[500],
        what: "Decades of maintenance information were shaped like a book. Nothing usable could be built until the equipment, not the document, was the root of the model.",
    },
    {
        to: "/work/Verizon",
        where: "Verizon",
        color: colors.coral[400],
        what: "Multiple teams solving the same interaction differently. Drift was the cost being paid, so the system had to become infrastructure rather than a style guide.",
    },
];

const VERIFY_STEPS = [
    { name: "Build the production bundle", color: colors.primary[500] },
    { name: "Serve the built artifact", color: colors.primary[500] },
    { name: "Drive it headless", color: colors.cyan[300] },
    { name: "Sweep every route", color: colors.cyan[300] },
    { name: "Measure geometry", color: colors.amber[400] },
    { name: "Read the screenshots", color: colors.emerald[500] },
];

const HowIBuild = () => (
    <PageWrapper>
        <Header>
            <PageHeadline>How I Build</PageHeadline>
            <Meta>
                <TerminalLabel>Working method</TerminalLabel>
                <span>Constraint &rarr; model &rarr; contract &rarr; slice &rarr; verify &rarr; systematise</span>
            </Meta>
            <Lede>
                Most of the work is not choosing what an interface looks like. It is deciding what
                the software has to be true about, then proving it still is once real conditions
                reach it. Everything below links to the work it came from, or to something in this
                site&rsquo;s own repository you can check &mdash;{" "}
                <strong>no practice claimed here that is not visible somewhere</strong>.
            </Lede>
        </Header>

        <Section>
            <SectionHeadline>Start From the Constraint</SectionHeadline>
            <Para>
                Every project on this site was shaped by one constraint that existed before any
                interface did. Find it and most of the architecture follows; miss it and you build
                something that demos well and fails in production.
            </Para>
            <EvidenceList>
                {CONSTRAINTS.map((row) => (
                    <EvidenceRow key={row.where} to={row.to}>
                        <Where color={row.color}>{row.where}</Where>
                        <What>{row.what}</What>
                        <Open>Open</Open>
                    </EvidenceRow>
                ))}
            </EvidenceList>
        </Section>

        <Section>
            <SectionHeadline>Model the Domain Before the Interface</SectionHeadline>
            <Para>
                Screens are the last thing I design, because a screen is a view onto a model and the
                model is the part that is expensive to get wrong. Caterpillar could not have an
                interactive maintenance procedure until equipment, systems, procedures, steps, parts,
                and service were separate entities. Verizon commerce could not be simplified until
                eligibility was something the product could ask about rather than something a screen
                described.
            </Para>
            <Pull>
                A screen built on the wrong model is not a design problem. It is a rewrite waiting to
                be scheduled.
            </Pull>
        </Section>

        <Section>
            <SectionHeadline>Make the Contract Explicit</SectionHeadline>
            <Para>
                Once more than one person depends on a decision, the decision needs a surface. That
                is true of a component API, and it is equally true of the agreement between a design
                system and the three teams consuming it. The difference between a specification that
                holds and one that erodes is usually whether it can be checked by something other
                than a person remembering it.
            </Para>
            <ContrastPair
                left={{
                    label: "Written down",
                    color: colors.coral[400],
                    text: "A convention documented in prose, re-litigated in every review",
                }}
                right={{
                    label: "Checkable",
                    color: colors.emerald[500],
                    text: "A rule with an identifier, a failure mode, and something that catches it",
                }}
            />
            <Para>
                It also decides the component boundary. Modelling appearance as separate components
                multiplies what you maintain; modelling it as properties keeps one implementation and
                pushes the permutations into the signature, where a consumer can discover them.
                Narrow the implementation, widen the parameters &mdash; the same reasoning as any
                other API.
            </Para>
        </Section>

        <Section>
            <SectionHeadline>Build the Thin Slice, Then Generalise</SectionHeadline>
            <Para>
                I do not build the abstraction first. I build the specific thing, then the next one,
                and let the repetition tell me where the seam actually is. Guessing at it early
                produces a shared component that fits nothing and has to be worked around.
            </Para>
            <Para>
                This site is the example. Four case studies were built with their own diagrams before
                anything was extracted &mdash; only once the same shapes had appeared four times did
                the vocabulary move into a shared primitives module. The two diagram files written
                after that contain <strong>no styling of their own at all</strong>; they are
                compositions and data. That is the argument the homepage makes about composition
                boundaries, applied to the repository making it.
            </Para>
        </Section>

        <Section>
            <SectionHeadline>Verify by Running It</SectionHeadline>
            <Para>
                A build that compiles has proven that the syntax parses. It has proven nothing about
                what a person sees. So the last step is always to run the real artifact and look at
                it &mdash; not the dev server, the production bundle.
            </Para>
            <DiagramSlot>
                <StepGrid
                    label="The Loop That Closes Every Change"
                    steps={VERIFY_STEPS}
                    columns={3}
                    caption="Every route driven at desktop and mobile widths, checked for horizontal overflow and runtime exceptions, with the geometry of anything suspicious measured directly rather than eyeballed."
                />
            </DiagramSlot>
            <Para>
                This is not ceremony. On this site alone that loop caught a development-only
                annotation shipping inside the production bundle, a metric value overflowing its card
                on a three-across grid, and a wrapped diagram row opening with a dangling connector.
                Every one of them compiled cleanly. None of them would have been found by reading the
                diff.
            </Para>
            <Pull>
                Compiling is not evidence. The only proof that software behaves is watching it
                behave.
            </Pull>
        </Section>

        <Section>
            <SectionHeadline>Systematise What Repeats</SectionHeadline>
            <Para>
                The last pass is asking what just got solved twice. Tokens exist so a value has a
                known blast radius instead of an unknown one: raw values, the semantic decisions
                referencing them, and the components consuming those decisions &mdash; components
                never reaching past the middle layer.
            </Para>
            <Para>
                That is testable here too. Retargeting this entire site to a terminal palette was a
                change to the token layer; the pages inherited it without being edited, because none
                of them had been allowed to name a colour directly.
            </Para>
            <DiagramSlot>
                <CycleFlow
                    label="The Working Loop"
                    nodes={[
                        { name: "Constraint", color: colors.primary[500] },
                        { name: "Domain Model", color: colors.cyan[300] },
                        { name: "Contract", color: colors.amber[400] },
                        { name: "Thin Slice", color: colors.emerald[500] },
                        { name: "Verify", color: colors.coral[400] },
                        { name: "Systematise", color: colors.purple[400] },
                    ]}
                    note="Each pass narrows what is still unknown, rather than adding another artifact"
                    noteColor={colors.purple[400]}
                />
            </DiagramSlot>
        </Section>

        <Section>
            <SectionHeadline>Where I Actually Spend the Time</SectionHeadline>
            <Para>
                Not in the tool. The expensive questions are the ones a mockup cannot answer: what
                does this component need to know, what happens when the content changes, what state
                is this in when the request fails, and can the pattern carry the next six cases
                without being forked. Answering those is engineering work regardless of which
                application it happens in.
            </Para>
            <Pull>
                A decision that cannot survive its own implementation is not a decision yet. It is a
                preference.
            </Pull>
        </Section>

        <NDADisclosure />
        <ContactForm />
    </PageWrapper>
);

export default HowIBuild;
